import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const WIDTH = "w-[46px] flex-none rounded-sm";
const SLOT = `h-[38px] ${WIDTH}`;

/**
 * A figure's drawing at the size of a row, on white in a thin frame. `alt` names it when nothing
 * beside it does; left out, the image is hidden from a reader.
 *
 * With no `src` it holds the same slot, so rows with and without a drawing line up: a quiet
 * ground while the drawing is not there, or the icon it is given for a row that never has one.
 * `dimmed` draws a figure that is set aside. `slim` is a line's slot, as wide as a drawing's
 * and only as tall as its icon, so a row of words stays a line high beside rows of drawings.
 */
export function Thumbnail({
  src,
  alt = "",
  dimmed = false,
  slim = false,
  children,
}: {
  src: string | undefined;
  alt?: string;
  dimmed?: boolean;
  slim?: boolean;
  children?: ReactNode;
}) {
  if (src !== undefined) {
    return (
      <img
        src={src}
        alt={alt}
        className={cn(
          SLOT,
          "border border-border bg-white object-cover",
          dimmed && "opacity-60",
        )}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        slim ? cn(WIDTH, "h-5") : SLOT,
        children === undefined
          ? "bg-muted"
          : "grid place-items-center text-subtle-foreground [&_svg]:size-4",
      )}
    >
      {children}
    </span>
  );
}
